import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useFirebase } from '../contexts/FirebaseContext';
import { Heart, Bookmark } from 'lucide-react';
import { getInitials } from '../utils/getInitials';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

interface Notification {
  id: string;
  type: 'like' | 'bookmark';
  fromUser: {
    id: string;
    displayName: string;
  };
  post: {
    id: string;
    content: string;
  };
  createdAt: any;
}

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useFirebase();

  useEffect(() => {
    if (!currentUser) return;

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('toUserId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newNotifications = snapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure all required fields exist with default values
        return {
          id: doc.id,
          type: data.type || 'like',
          fromUser: {
            id: data.fromUser?.id || '',
            displayName: data.fromUser?.displayName || 'Unknown User'
          },
          post: {
            id: data.post?.id || '',
            content: data.post?.content || 'Post content unavailable'
          },
          createdAt: data.createdAt,
          ...data
        };
      }) as Notification[];

      setNotifications(newNotifications);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  if (!currentUser) return null;

  return (
    <div>
      <div className="border-b border-gray-800 sticky top-0 bg-gray-900/80 backdrop-blur-sm z-10">
        <h1 className="text-xl font-bold p-4">Notifications</h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">No notifications yet</p>
          <p className="text-sm">When someone interacts with your posts, you'll see it here</p>
        </div>
      ) : (
        <div>
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="flex items-start space-x-4 p-4 border-b border-gray-800 hover:bg-gray-800/50"
            >
              {/* User Avatar */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                  {getInitials(notification.fromUser?.displayName || 'Unknown User')}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <Link 
                    to={`/profile/${notification.fromUser?.id || '#'}`}
                    className="font-medium hover:underline"
                  >
                    {notification.fromUser?.displayName || 'Unknown User'}
                  </Link>
                  <span className="text-gray-500">
                    {notification.type === 'like' ? 'liked' : 'bookmarked'} your post
                  </span>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-500">
                    {notification.createdAt && formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })}
                  </span>
                </div>

                {/* Post Preview */}
                {notification.post?.id && (
                  <Link 
                    to={`/post/${notification.post.id}`} 
                    className="mt-2 text-gray-400 hover:text-gray-300 block"
                  >
                    {notification.post.content || 'Post content unavailable'}
                  </Link>
                )}

                {/* Icon */}
                <div className="mt-2">
                  {notification.type === 'like' ? (
                    <Heart className="w-5 h-5 text-red-500 fill-current" />
                  ) : (
                    <Bookmark className="w-5 h-5 text-purple-500 fill-current" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;